import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ToastProvider";
import { useEventSettlement } from "@/hooks/useEventSettlement";
import { SectionError } from "@/components/ui/error-ui";
import { getManagerEvents, importManagerEvents, updateManagerEvent } from "@/api/manager";
import { createLedgerAccount, fetchLedgerAccounts } from "@/api/ledger";
import type { LedgerAccount } from "@/types/ledger";

const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

const normalizeNumeric = (value: number | string | null | undefined) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value === "bigint") {
    return Number(value);
  }
  return 0;
};

const formatSettlementCurrency = (value: number | string | null | undefined) => currency.format(normalizeNumeric(value));

const formatPercent = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${(Number(value) * 100).toFixed(1)}%`;
};

type ManagerEvent = {
  id: string;
  name?: string;
  event_date?: string;
  city?: string;
  state?: string;
  attendance?: number | null;
  venue?: string | null;
  source?: string | null;
  ticket_gross?: number | null;
  merch_revenue?: number | null;
  revenue?: number | null;
  guarantee_amount?: number | null;
  split_terms?: string | null;
  artist_percentage?: number | null;
  sound_expenses?: number | null;
  house_expenses?: number | null;
  notes?: string | null;
};

type Props = {
  standalone?: boolean;
};

export function EventSettlementPanel({ standalone = false }: Props) {
  const { eventId } = useParams<{ eventId?: string }>();
  const navigate = useNavigate();
  const { push } = useToast();
  const [events, setEvents] = useState<ManagerEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventError, setEventError] = useState<string | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [syncingEvents, setSyncingEvents] = useState(false);
  const [costForm, setCostForm] = useState({ category: "Sound", amount: "", memo: "" });
  const [incomeForm, setIncomeForm] = useState({ source: "Merch", amount: "", memo: "", ticketGross: "", splitPct: "" });
  const [selection, setSelection] = useState<string | null>(null);
  const [ticketsSold, setTicketsSold] = useState("");
  const [savingTickets, setSavingTickets] = useState(false);
  const [eventNotes, setEventNotes] = useState("");
  const [ledgerAccounts, setLedgerAccounts] = useState<LedgerAccount[]>([]);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [ledgerError, setLedgerError] = useState<string | null>(null);
  const [newExpenseCategory, setNewExpenseCategory] = useState("");
  const [addingExpenseCategory, setAddingExpenseCategory] = useState(false);

  const routeEventId = standalone ? eventId ?? null : null;
  const activeEventId = routeEventId ?? selection ?? (events[0]?.id ?? null);
  const {
    costs,
    allocations,
    settlement,
    loading,
    error: settlementError,
    savingCost,
    savingAllocation,
    rollingUp,
    deletingCostId,
    deletingAllocationId,
    createCost,
    createAllocation,
    removeCost,
    removeAllocation,
    rollup,
  } = useEventSettlement(activeEventId);

  useEffect(() => {
    const load = async () => {
      setLoadingEvents(true);
      setEventError(null);
      try {
        const payload = await getManagerEvents(50);
        const list = Array.isArray(payload?.events) ? payload.events : Array.isArray(payload) ? payload : [];
        setEvents(list);
        const bandsintownMeta = (payload?.bandsintown ?? {}) as { last_sync_at?: string | null };
        setLastSyncAt(bandsintownMeta?.last_sync_at ?? null);
        if (standalone) {
          if (!eventId && list[0]) {
            navigate(`/events/${list[0].id}/settlement`, { replace: true });
          }
        } else {
          setSelection((prev) => prev ?? list[0]?.id ?? null);
        }
      } catch (err) {
        setEventError(err instanceof Error ? err.message : "Failed to load events");
      } finally {
        setLoadingEvents(false);
      }
    };
    void load();
  }, [eventId, navigate, standalone]);

  const selectedEvent = useMemo(() => events.find((evt) => evt.id === activeEventId) ?? null, [events, activeEventId]);

  useEffect(() => {
    if (!selectedEvent) {
      setTicketsSold("");
      setEventNotes("");
      return;
    }
    setTicketsSold(selectedEvent.attendance != null ? String(selectedEvent.attendance) : "");
    setEventNotes(selectedEvent.notes ?? "");
  }, [selectedEvent]);

  const loadLedgerAccounts = useCallback(async () => {
    setLoadingLedger(true);
    setLedgerError(null);
    try {
      const accounts = await fetchLedgerAccounts();
      setLedgerAccounts(Array.isArray(accounts) ? accounts : []);
    } catch (err) {
      setLedgerError(err instanceof Error ? err.message : "Failed to load ledger accounts");
    } finally {
      setLoadingLedger(false);
    }
  }, []);

  useEffect(() => {
    void loadLedgerAccounts();
  }, [loadLedgerAccounts]);

  const handleSelectEvent = (value: string) => {
    if (!value) return;
    if (standalone) {
      navigate(`/events/${value}/settlement`);
    } else {
      setSelection(value);
    }
  };

  const handleSyncEvents = async () => {
    setSyncingEvents(true);
    try {
      const payload = await importManagerEvents({ date_filter: "all" });
      if (payload?.last_sync_at) {
        setLastSyncAt(payload.last_sync_at);
      }
      const refreshed = await getManagerEvents(50);
      const list = Array.isArray(refreshed?.events) ? refreshed.events : Array.isArray(refreshed) ? refreshed : [];
      setEvents(list);
    } catch (err) {
      push({ title: "Bandsintown sync failed", message: err instanceof Error ? err.message : "Unable to sync events." });
    } finally {
      setSyncingEvents(false);
    }
  };

  const handleSaveTickets = async () => {
    if (!activeEventId) return;
    setSavingTickets(true);
    try {
      const parsed = ticketsSold.trim() === "" ? null : Number(ticketsSold);
      if (parsed != null && !Number.isFinite(parsed)) {
        throw new Error("Tickets sold must be a number.");
      }
      await updateManagerEvent(activeEventId, { attendance: parsed, notes: eventNotes.trim() || null });
      const refreshed = await getManagerEvents(50);
      const list = Array.isArray(refreshed?.events) ? refreshed.events : Array.isArray(refreshed) ? refreshed : [];
      setEvents(list);
      push({ title: "Tickets sold saved", message: selectedEvent?.name ?? "Event" });
    } catch (err) {
      push({ title: "Tickets sold save failed", message: err instanceof Error ? err.message : "Unable to save tickets sold." });
    } finally {
      setSavingTickets(false);
    }
  };

  const submitCost = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!activeEventId || !costForm.amount) return;
    try {
      await createCost({ category: costForm.category, amount: Number(costForm.amount), memo: costForm.memo || undefined });
      await rollup();
      push({ title: "Cost recorded", message: costForm.category });
      setCostForm({ ...costForm, amount: "", memo: "" });
    } catch (err) {
      push({ title: "Cost save failed", message: err instanceof Error ? err.message : "Unable to save this cost." });
    }
  };

  const submitAllocation = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!activeEventId) return;
    const source = incomeForm.source.trim();
    if (!source) return;
    const isTicketIncome = source.toLowerCase().includes("ticket");
    const gross = Number(incomeForm.ticketGross);
    const splitPct = Number(incomeForm.splitPct);
    const amount = isTicketIncome ? (Number.isFinite(gross) && Number.isFinite(splitPct) ? (gross * splitPct) / 100 : NaN) : Number(incomeForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) return;
    try {
      await createAllocation({
        bucket: resolveIncomeBucket(source),
        label: source,
        amount,
        memo: incomeForm.memo || undefined,
      });
      await rollup();
      push({ title: "Income added", message: source });
      setIncomeForm((prev) => ({ ...prev, amount: "", memo: "", ticketGross: "", splitPct: "" }));
    } catch (err) {
      push({ title: "Income save failed", message: err instanceof Error ? err.message : "Unable to save this income." });
    }
  };

  const handleAddExpenseCategory = async () => {
    const trimmed = newExpenseCategory.trim();
    if (!trimmed) return;
    setAddingExpenseCategory(true);
    try {
      const code = nextExpenseCode(ledgerAccounts);
      await createLedgerAccount({ code, name: trimmed, type: "expense" });
      setNewExpenseCategory("");
      await loadLedgerAccounts();
      setCostForm((prev) => ({ ...prev, category: trimmed }));
    } catch (err) {
      push({ title: "Expense category failed", message: err instanceof Error ? err.message : "Unable to add expense category." });
    } finally {
      setAddingExpenseCategory(false);
    }
  };

  const handleRollup = async () => {
    await rollup();
    push({ title: "Settlement updated", message: selectedEvent?.name ?? "Event" });
  };

  return (
    <div className="space-y-6 rounded-2xl border border-border bg-background/70 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-primary-foreground dark:text-amber-100">Event profitability</h2>
          <p className="text-sm text-muted-foreground">Tag ledger activity with event IDs, then review per-show P&L.</p>
          {lastSyncAt && <p className="text-xs text-muted-foreground">Bandsintown sync {new Date(lastSyncAt).toLocaleString()}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <select
            value={activeEventId ?? ""}
            onChange={(event) => handleSelectEvent(event.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-foreground"
            disabled={loadingEvents}
          >
            <option value="" disabled>
              {loadingEvents ? "Loading events…" : "Select event"}
            </option>
            {events.map((evt) => (
              <option key={evt.id} value={evt.id}>
                {formatEventLabel(evt)}
              </option>
            ))}
          </select>
          <Button variant="outline" onClick={handleSyncEvents} disabled={syncingEvents}>
            {syncingEvents ? "Syncing…" : "Sync Bandsintown"}
          </Button>
          <Button variant="outline" onClick={handleRollup} disabled={!activeEventId || rollingUp}>
            {rollingUp ? "Calculating…" : "Recalculate"}
          </Button>
        </div>
      </div>
      {eventError && <SectionError message={eventError} />}
      {settlementError && <SectionError message={settlementError} />}
      {!activeEventId && !loadingEvents && <p className="text-sm text-muted-foreground">Select an event to begin.</p>}
      {activeEventId && (
        <div className="space-y-6">
          <section className="rounded-2xl border border-border bg-background/70 p-5">
            <details className="group" open>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-2">
                <div>
                  <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Show overview + profitability</h3>
                  <p className="text-xs text-muted-foreground">
                    {selectedEvent ? formatEventLabel(selectedEvent) : "—"}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {selectedEvent?.source && (
                    <span className="rounded-full border border-border/70 px-3 py-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                      {selectedEvent.source}
                    </span>
                  )}
                  <span className="rounded-full border border-border/70 px-3 py-1 text-[11px] uppercase tracking-wide text-muted-foreground transition group-open:rotate-180">
                    ▼
                  </span>
                </div>
              </summary>
              <div className="mt-4 space-y-5">
                <section className="grid gap-3 sm:grid-cols-3">
                  <MetricCard title="Net profit" value={settlement ? formatSettlementCurrency(settlement.profit) : "—"} description="Performance + merch minus costs" />
                  <MetricCard
                    title="Merch margin"
                    value={settlement?.merch_margin != null ? formatPercent(settlement.merch_margin) : "—"}
                    description="(Merch - COGS) / Merch"
                  />
                  <MetricCard
                    title="Profit / mile"
                    value={settlement?.profit_per_mile != null ? formatSettlementCurrency(settlement.profit_per_mile) : "—"}
                    description="Requires miles in event metadata"
                  />
                </section>
                <div className="grid gap-2 grid-cols-2 sm:grid-cols-3">
                  <SummaryCard label="Guarantee" value={formatSettlementCurrency(selectedEvent?.guarantee_amount)} />
                  <SummaryCard
                    label="Artist split"
                    value={
                      selectedEvent?.artist_percentage != null
                        ? `${Number(selectedEvent.artist_percentage).toFixed(0)}%`
                        : "—"
                    }
                  />
                  <SummaryCard label="Split terms" value={selectedEvent?.split_terms || "—"} />
                  <SummaryCard label="House expenses" value={formatSettlementCurrency(selectedEvent?.house_expenses)} />
                  <SummaryCard label="Sound expenses" value={formatSettlementCurrency(selectedEvent?.sound_expenses)} />
                  <SummaryCard
                    label="Net revenue"
                    value={formatSettlementCurrency(
                      typeof selectedEvent?.revenue === "number"
                        ? selectedEvent.revenue - ((selectedEvent.house_expenses ?? 0) + (selectedEvent.sound_expenses ?? 0))
                        : null,
                    )}
                  />
                  <SummaryCard label="Ticket gross" value={formatSettlementCurrency(selectedEvent?.ticket_gross)} />
                  <SummaryCard label="Merch revenue" value={formatSettlementCurrency(selectedEvent?.merch_revenue)} />
                  <SummaryCard label="Notes" value={selectedEvent?.notes || "—"} />
                </div>
              </div>
            </details>
          </section>
          <section className="rounded-2xl border border-border bg-background/70 p-5">
            <header className="mb-4 flex flex-wrap items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Tickets sold</h3>
                <p className="text-xs text-muted-foreground">Used for ticket history reports. Does not affect accounting.</p>
              </div>
            </header>
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-xs">
                <span>Tickets sold</span>
                <input
                  type="number"
                  min={0}
                  value={ticketsSold}
                  onChange={(event) => setTicketsSold(event.target.value)}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </label>
              <label className="flex flex-1 flex-col gap-1 text-xs">
                <span>Notes</span>
                <input
                  type="text"
                  value={eventNotes}
                  onChange={(event) => setEventNotes(event.target.value)}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                />
              </label>
              <Button onClick={handleSaveTickets} disabled={savingTickets || !activeEventId}>
                {savingTickets ? "Saving…" : "Save tickets"}
              </Button>
            </div>
          </section>
          <section className="rounded-2xl border border-border bg-background/70 p-5">
            <header className="mb-4 flex flex-wrap items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Manual costs</h3>
                <p className="text-xs text-muted-foreground">Use this for venue settlements, per diems, etc.</p>
              </div>
              <span className="text-xs text-muted-foreground">{costs.length} records</span>
            </header>
            <DataTable
              emptyState="No manual costs yet"
              headers={["Category", "Memo", "Amount", ""]}
              rows={costs.map((cost) => [
                cost.category ?? "—",
                cost.memo || "—",
                formatSettlementCurrency(cost.amount),
                <ActionButton
                  key={cost.id}
                  label="Remove cost"
                  disabled={deletingCostId === cost.id}
                  onClick={() => removeCost(cost.id)}
                />,
              ])}
            />
            <form onSubmit={submitCost} className="mt-4 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
              <label className="flex flex-col gap-1 text-xs">
                <span>Category</span>
                <select
                  value={costForm.category}
                  onChange={(event) => setCostForm((prev) => ({ ...prev, category: event.target.value }))}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                >
                  {expenseOptions(ledgerAccounts).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              <InputField label="Amount" type="number" value={costForm.amount} onChange={(value) => setCostForm((prev) => ({ ...prev, amount: value }))} />
              <InputField label="Memo" value={costForm.memo} onChange={(value) => setCostForm((prev) => ({ ...prev, memo: value }))} />
              <Button type="submit" disabled={savingCost || loading}>
                {savingCost ? "Saving…" : "Add cost"}
              </Button>
            </form>
            <div className="mt-3 flex flex-wrap items-end gap-3 text-xs text-muted-foreground">
              <div className="flex flex-1 flex-col gap-1">
                <span>Add new expense category</span>
                <input
                  type="text"
                  value={newExpenseCategory}
                  onChange={(event) => setNewExpenseCategory(event.target.value)}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                  placeholder="New expense category"
                />
              </div>
              <Button
                variant="outline"
                onClick={handleAddExpenseCategory}
                disabled={addingExpenseCategory || loadingLedger || !newExpenseCategory.trim()}
              >
                {addingExpenseCategory ? "Adding…" : "Add category"}
              </Button>
              {ledgerError && <span className="text-xs text-destructive">{ledgerError}</span>}
            </div>
          </section>
          <section className="rounded-2xl border border-border bg-background/70 p-5">
            <header className="mb-4 flex flex-wrap items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Income</h3>
                <p className="text-xs text-muted-foreground">Track earned revenue and tips that should land in artist settlement.</p>
              </div>
              <span className="text-xs text-muted-foreground">{allocations.length} allocations</span>
            </header>
            <DataTable
              emptyState="No income yet"
              headers={["Income", "Memo", "Amount", ""]}
              rows={allocations.map((allocation) => [
                allocation.label ?? "—",
                allocation.memo || "—",
                formatSettlementCurrency(allocation.amount),
                <ActionButton
                  key={allocation.id}
                  label="Remove income"
                  disabled={deletingAllocationId === allocation.id}
                  onClick={() => removeAllocation(allocation.id)}
                />,
              ])}
            />
            <form onSubmit={submitAllocation} className="mt-4 grid gap-3 sm:grid-cols-2 md:grid-cols-4">
              <label className="flex flex-col gap-1 text-xs">
                <span>Income type</span>
                <select
                  value={incomeForm.source}
                  onChange={(event) => setIncomeForm((prev) => ({ ...prev, source: event.target.value }))}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                >
                  {incomeOptions(ledgerAccounts).map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
              {isTicketIncome(incomeForm.source) ? (
                <InputField
                  label="Ticket gross"
                  type="number"
                  value={incomeForm.ticketGross}
                  onChange={(value) => setIncomeForm((prev) => ({ ...prev, ticketGross: value }))}
                />
              ) : (
                <InputField
                  label="Amount"
                  type="number"
                  value={incomeForm.amount}
                  onChange={(value) => setIncomeForm((prev) => ({ ...prev, amount: value }))}
                />
              )}
              {isTicketIncome(incomeForm.source) ? (
                <InputField
                  label="Split %"
                  type="number"
                  value={incomeForm.splitPct}
                  onChange={(value) => setIncomeForm((prev) => ({ ...prev, splitPct: value }))}
                />
              ) : null}
              {isTicketIncome(incomeForm.source) ? (
                <InputField
                  label="Artist amount"
                  value={formatTicketAmount(incomeForm.ticketGross, incomeForm.splitPct)}
                  onChange={() => {}}
                  readOnly
                />
              ) : null}
              <InputField label="Memo" value={incomeForm.memo} onChange={(value) => setIncomeForm((prev) => ({ ...prev, memo: value }))} />
              <Button type="submit" disabled={savingAllocation || loading}>
                {savingAllocation ? "Saving…" : "Add income"}
              </Button>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}

const formatEventLabel = (event: ManagerEvent) => {
  const date = event.event_date ? new Date(event.event_date).toLocaleDateString() : "Date TBA";
  const location = [event.city, event.state].filter(Boolean).join(", ");
  return `${date} · ${event.name ?? "Untitled"}${location ? ` (${location})` : ""}`;
};

type DataTableProps = {
  headers: string[];
  rows: ReactNode[][];
  emptyState: string;
};

function DataTable({ headers, rows, emptyState }: DataTableProps) {
  const normalizedRows = rows.map((row) => row.map((cell) => (cell == null || cell === "" ? "—" : cell)));
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-background/40 text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            {headers.map((header) => (
              <th key={header} className="px-3 py-2 text-left">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {normalizedRows.map((row, idx) => (
            <tr key={idx} className="border-t border-border">
              {row.map((cell, cellIndex) => (
                <td key={`${idx}-${cellIndex}`} className={`px-3 py-2 ${cellIndex === row.length - 1 ? "text-right" : "text-left"}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={headers.length} className="px-3 py-3 text-center text-xs text-muted-foreground">
                {emptyState}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

type InputFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  readOnly?: boolean;
};

function InputField({ label, value, onChange, type = "text", readOnly = false }: InputFieldProps) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span>{label}</span>
      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
        readOnly={readOnly}
      />
    </label>
  );
}
type MetricCardProps = {
  title: string;
  value: string;
  description: string;
};

function MetricCard({ title, value, description }: MetricCardProps) {
  return (
    <article className="rounded-2xl border border-border bg-background/70 p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{title}</p>
      <p className="text-2xl font-semibold text-primary-foreground dark:text-amber-100">{value}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </article>
  );
}

type SummaryCardProps = {
  label: string;
  value: string;
};

function SummaryCard({ label, value }: SummaryCardProps) {
  return (
    <div className="rounded-xl border border-border/60 bg-background/30 p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80">{label}</p>
      <p className="text-base font-semibold text-foreground">{value}</p>
    </div>
  );
}

type ActionButtonProps = {
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

function ActionButton({ label, onClick, disabled }: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-xs text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
      aria-label={label}
      title={label}
    >
      ×
    </button>
  );
}

const EXPENSE_DEFAULTS = ["Sound", "Venue", "Meals", "Travel", "Lodging", "Production", "Other"];
const INCOME_DEFAULTS = ["Merch", "Guarantee", "Ticket Income", "Tips"];

const expenseOptions = (accounts: LedgerAccount[]) => {
  const ledger = accounts.filter((acct) => acct.type === "expense" && acct.is_active).map((acct) => acct.name);
  return dedupeOptions([...EXPENSE_DEFAULTS, ...ledger]);
};

const incomeOptions = (accounts: LedgerAccount[]) => {
  const ledger = accounts.filter((acct) => acct.type === "income" && acct.is_active).map((acct) => acct.name);
  return dedupeOptions([...INCOME_DEFAULTS, ...ledger]);
};

const dedupeOptions = (values: string[]) => {
  const seen = new Set<string>();
  return values.filter((value) => {
    const key = value.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const isTicketIncome = (source: string) => source.trim().toLowerCase().includes("ticket");

const resolveIncomeBucket = (source: string): "performance" | "merch" | "cogs" | "expense" => {
  const lowered = source.toLowerCase();
  if (lowered.includes("merch")) return "merch";
  return "performance";
};

const formatTicketAmount = (grossValue: string, splitValue: string) => {
  const gross = Number(grossValue);
  const split = Number(splitValue);
  if (!Number.isFinite(gross) || !Number.isFinite(split)) return "—";
  return formatSettlementCurrency((gross * split) / 100);
};

const nextExpenseCode = (accounts: LedgerAccount[]) => {
  const numericCodes = accounts
    .filter((acct) => acct.type === "expense")
    .map((acct) => Number.parseInt(acct.code, 10))
    .filter((value) => Number.isFinite(value));
  if (numericCodes.length === 0) return "5000";
  const max = Math.max(...numericCodes);
  return String(max + 10);
};
